import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "./Header";
import { API_BASE_URL } from "../utils/apiConfig";
import Swal from "sweetalert2";

export default function LogoPage() {
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [currentLogos, setCurrentLogos] = useState(null);
  const [mainLogoFile, setMainLogoFile] = useState(null);
  const [subLogoFile, setSubLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/api/devices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDevices(response.data);
        if (response.data.length > 0) {
          setSelectedDeviceId(response.data[0].device_id_PK);
        }
      } catch (err) {
        console.error("Failed to fetch devices", err);
      }
    };
    fetchDevices();
  }, []);

  useEffect(() => {
    if (selectedDeviceId) {
      fetchCurrentLogos(selectedDeviceId);
    }
  }, [selectedDeviceId]);

  const fetchCurrentLogos = async (deviceId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/logos/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status && response.data.data) {
        setCurrentLogos(response.data.data);
      } else {
        setCurrentLogos(null);
      }
    } catch (err) {
      setCurrentLogos(null);
      console.error("Failed to fetch logos for device", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!mainLogoFile && !subLogoFile) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Files',
        text: 'Please select at least one logo to upload.',
        background: '#1b1d1a',
        color: '#fff',
      });
      return;
    }

    const formData = new FormData();
    formData.append("device_id", selectedDeviceId);
    if (mainLogoFile) formData.append("mainlogo", mainLogoFile);
    if (subLogoFile) formData.append("sublogo", subLogoFile);

    try {
      const token = localStorage.getItem("token");
      Swal.fire({
        title: 'Uploading...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
        background: '#1b1d1a',
        color: '#fff',
      });

      await axios.post(`${API_BASE_URL}/api/logos`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          // boundary automatically added by axios
        }
      });

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Logos have been updated successfully.',
        background: '#1b1d1a',
        color: '#fff',
        confirmButtonColor: '#22c55e'
      });

      // Clear files
      setMainLogoFile(null);
      setSubLogoFile(null);

      // Refresh current logos
      fetchCurrentLogos(selectedDeviceId);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: 'An error occurred while uploading logos.',
        background: '#1b1d1a',
        color: '#fff',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  return (
    <div className="bg-black min-h-screen text-white p-6">
      <Header />

      <div className=" mx-auto mt-8">
        <h2 className="text-3xl font-bold text-green-500 tracking-tight mb-6">Manage Device Logos</h2>

        <div className="bg-[#111211] border border-gray-800 rounded-2xl p-6 shadow-2xl">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-400 mb-2">Select Machine</label>
            <select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              className="w-full bg-[#1b1d1a] border border-gray-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 transition"
            >
              {devices.map(device => (
                <option key={device.device_id_PK} value={device.device_id_PK}>
                  {device.device_name}{device.user_location ? ` (${device.user_location})` : (device.location ? ` (${device.location})` : '')}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-[#1b1d1a] border border-gray-800 rounded-xl p-4 text-center">
              <h3 className="text-sm font-semibold text-gray-400 mb-4 uppercase tracking-widest">Current Main Logo</h3>
              <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-700 rounded-lg overflow-hidden bg-black/50">
                {loading ? (
                  <span className="text-gray-600">Loading...</span>
                ) : currentLogos?.mainlogo ? (
                  <img src={currentLogos.mainlogo} alt="Main Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-gray-600">No Main Logo Set</span>
                )}
              </div>
            </div>

            <div className="bg-[#1b1d1a] border border-gray-800 rounded-xl p-4 text-center">
              <h3 className="text-sm font-semibold text-gray-400 mb-4 uppercase tracking-widest">Current Sub Logo</h3>
              <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-700 rounded-lg overflow-hidden bg-black/50">
                {loading ? (
                  <span className="text-gray-600">Loading...</span>
                ) : currentLogos?.sublogo ? (
                  <img src={currentLogos.sublogo} alt="Sub Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-gray-600">No Sub Logo Set</span>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleUpload} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Upload New Main Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setMainLogoFile(e.target.files[0])}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20 transition cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Upload New Sub Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSubLogoFile(e.target.files[0])}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20 transition cursor-pointer"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-4 rounded-xl transition duration-300 transform hover:scale-[1.02] shadow-[0_0_15px_rgba(34,197,94,0.3)]"
            >
              Save Logos
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
